import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import { RevealLines, Reveal } from "../components/Reveal";
import { Check, Loader2 } from "lucide-react";

const inr = (paise) => "₹" + (paise / 100).toLocaleString("en-IN");

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState({ enabled: false });
  const [payingId, setPayingId] = useState(null);
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
    loadRazorpay();
  }, []);

  const startPayment = async ({ package_id, custom_amount, label }) => {
    if (!user) { toast.info("Please sign in to continue to payment."); navigate("/login", { state: { from: "/pricing" } }); return; }
    const key = package_id || "custom";
    setPayingId(key);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway."); return; }
      const { data } = await api.post("/payments/order", { package_id, custom_amount });
      const rz = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Joy Resume Builders & Consulting",
        description: data.item,
        order_id: data.order_id,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#1D4ED8" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — thank you!");
            navigate("/dashboard");
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed.");
          }
        },
        modal: { ondismiss: () => setPayingId(null) },
      });
      rz.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed."));
      rz.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment.");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="pricing-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-28">
        <span className="overline text-cobalt">Pricing</span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-navy mt-5 leading-[0.95] max-w-3xl">
          <RevealLines lines={["Invest in the", "career you want."]} />
        </h1>
        <p className="text-slateink text-lg mt-6 max-w-xl">
          Transparent, fixed pricing. Secure checkout via Razorpay — cards, UPI, netbanking & wallets.
        </p>

        {!config.enabled && (
          <div className="mt-8 border border-cobalt/30 bg-cobalt/5 text-navy px-5 py-4 text-sm" data-testid="pricing-config-warning">
            Payments are in setup mode. Add your Razorpay test keys in the backend to enable live checkout.
          </div>
        )}

        {/* Packages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07}>
              <div data-testid={`pricing-card-${p.id}`} className="h-full bg-white border border-border p-7 flex flex-col hover:border-navy transition-colors">
                <h3 className="font-display text-2xl font-semibold text-navy">{p.name}</h3>
                <p className="text-sm text-slateink mt-2 leading-relaxed flex-1">{p.desc}</p>
                <p className="font-display text-4xl font-bold text-navy mt-6">{inr(p.amount)}</p>
                <button data-testid={`pay-${p.id}`} disabled={payingId === p.id}
                  onClick={() => startPayment({ package_id: p.id, label: p.name })}
                  className="mt-5 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-60">
                  {payingId === p.id ? <><Loader2 size={16} className="animate-spin" /> Opening…</> : "Pay & get started"}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Custom amount */}
        <Reveal delay={0.1}>
          <div className="mt-6 bg-navy text-white grain relative p-8 lg:p-10 grid lg:grid-cols-[1fr_auto] gap-6 items-center" data-testid="pricing-custom">
            <div className="relative z-10">
              <span className="overline text-cobalt-light">Deposit / custom</span>
              <h3 className="font-display text-3xl font-semibold mt-3">Pay any amount</h3>
              <p className="text-white/60 mt-2 max-w-md">Paying a retainer, a deposit, or a custom-quoted engagement? Enter the amount agreed with your consultant.</p>
            </div>
            <div className="relative z-10 flex gap-3">
              <div className="flex items-center bg-white/10 border border-white/20 px-4">
                <span className="text-white/60">₹</span>
                <input data-testid="custom-amount-input" type="number" min="500" value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)} placeholder="500"
                  className="w-32 bg-transparent outline-none text-white px-2 py-3.5 placeholder:text-white/30" />
              </div>
              <button data-testid="pay-custom" disabled={payingId === "custom" || !customAmount}
                onClick={() => startPayment({ custom_amount: parseInt(customAmount, 10), label: "Custom payment" })}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cobalt text-white font-medium hover:bg-white hover:text-navy transition-colors disabled:opacity-50">
                {payingId === "custom" ? <><Loader2 size={16} className="animate-spin" /> Opening…</> : "Pay now"}
              </button>
            </div>
          </div>
        </Reveal>

        <p className="text-xs text-slateink/70 mt-6 flex items-center gap-2">
          <Check size={14} className="text-cobalt" /> Secure payments · You'll be asked to sign in before checkout.
        </p>
      </div>
    </div>
  );
}
